import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fmtDate, fmtNum } from "../lib/format";
import { canOperationalApprove, useAuth } from "../lib/auth";
import RecoveryDialog from "../components/RecoveryDialog";

export default function Discrepancies() {
  const { user } = useAuth();
  const [data, setData] = useState({
    shortage_reports: [],
    escalated_tasks: [],
  });
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [recoverMiss, setRecoverMiss] = useState(null);

  const load = async () => {
    const [d, i, r] = await Promise.all([
      api.get("/discrepancies"),
      api.get("/items"),
      api.get("/rooms"),
    ]);
    setData(d.data);
    setItems(i.data);
    setRooms(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const itemName = (id) => items.find((x) => x.id === id)?.name || id;
  const roomNum = (id) => rooms.find((x) => x.id === id)?.number || "—";

  const approveLoss = async (r) => {
    if (
      !window.confirm(
        `Approve ${r.qty} × as an audited loss (permanent stock deduction)?`,
      )
    )
      return;
    try {
      await api.post("/missing-reports/approve-loss", {
        report_id: r.id,
        remarks: "Resolved from Discrepancy Review",
      });
      toast.success("Loss approved (audited)");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const submitRecover = async (payload) => {
    await api.post("/missing-reports/recover", {
      report_id: recoverMiss.id,
      ...payload,
    });
    toast.success("Recovered");
    load();
  };

  return (
    <Layout>
      <h1 className="section-title" data-testid="page-title">
        Discrepancy Review
      </h1>
      <div className="section-sub mb-4">
        Unresolved checkout shortages and possible-loss flags in one place.
        Resolve each with an audited adjustment — Approve Loss (permanent
        deduction) or Recovered.
      </div>

      <Card className="p-0 overflow-hidden mb-4">
        <div className="p-2 text-sm font-medium text-slate-700 border-b border-slate-100">
          Shortage / Possible Loss ({data.shortage_reports.length})
        </div>
        <div className="overflow-x-auto">
          <table className="vr-table" data-testid="discrepancy-table">
            <thead>
              <tr>
                <th>Reported</th>
                <th>Item</th>
                <th className="num">Qty</th>
                <th>Room</th>
                <th>Reason / Note</th>
                <th className="no-print">Resolve (audited)</th>
              </tr>
            </thead>
            <tbody>
              {data.shortage_reports.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.submitted_at || r.created_at)}</td>
                  <td>{itemName(r.item_id)}</td>
                  <td className="num">{fmtNum(r.qty)}</td>
                  <td>{r.room_id ? `Room ${roomNum(r.room_id)}` : "—"}</td>
                  <td>{r.note || r.reason || "—"}</td>
                  <td className="no-print flex flex-wrap gap-2">
                    {canOperationalApprove(user) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => approveLoss(r)}
                        data-testid={`disc-loss-${r.id}`}
                      >
                        Approve Loss
                      </Button>
                    )}
                    {canOperationalApprove(user) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRecoverMiss(r)}
                        data-testid={`disc-recover-${r.id}`}
                      >
                        Recovered
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {data.shortage_reports.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-500 py-6">
                    No open shortage discrepancies.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="p-2 text-sm font-medium text-slate-700 border-b border-slate-100">
          Escalated Checkouts ({data.escalated_tasks.length})
        </div>
        <div className="overflow-x-auto">
          <table className="vr-table" data-testid="escalated-table">
            <thead>
              <tr>
                <th>Triggered</th>
                <th>Room</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              {data.escalated_tasks.map((t) => (
                <tr key={t.id}>
                  <td>{fmtDate(t.triggered_at)}</td>
                  <td>Room {roomNum(t.room_id)}</td>
                  <td className="text-xs text-amber-700">
                    Verified quantity exceeded the recorded room balance — needs
                    an audited Admin adjustment before collection.
                  </td>
                </tr>
              ))}
              {data.escalated_tasks.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-slate-500 py-6">
                    No escalated checkouts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <RecoveryDialog
        open={!!recoverMiss}
        onOpenChange={(o) => !o && setRecoverMiss(null)}
        title={recoverMiss ? `Recover · ${itemName(recoverMiss.item_id)}` : ""}
        maxQty={recoverMiss?.qty || 1}
        destinationOptions={[
          { value: "STORE_CLEAN", label: "Store Clean" },
          { value: "ROOM_CLEAN", label: "Back to Room" },
        ]}
        rooms={rooms}
        onSubmit={submitRecover}
      />
    </Layout>
  );
}
